import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
    AfterViewInit,
    Component,
    ContentChildren,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { difference } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CommonTexts } from "~app/common/texts/common.texts";
import { BusySpinnerComponent } from "../busy/busy-spinner/busy-spinner.component";
import { IconComponent } from "../icon/icon.component";
import { ITab } from "./tab/itab";
import { TabComponent } from "./tab/tab.component";

@Component({
    selector: "rask-tabs",
    styleUrls: ["./tabs.component.scss"],
    templateUrl: "./tabs.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        IconComponent,
        NgTemplateOutlet,
        NgbPopover,
        BusySpinnerComponent
    ]
})
export class TabsComponent implements AfterViewInit, OnChanges, OnDestroy {
    activeTab: ITab;
    @Input() autoSelectNewTab = false;
    @Input() closable = false;
    @Input() closeIcon = "";
    @Input() closeIconColor = "";
    commonTexts = CommonTexts.texts;
    @ContentChildren(TabComponent) contentTabs: QueryList<TabComponent>;
    @Input() initialTabId?: any;
    initialized = false;
    @Input() isQueryParam = true;
    @Input() layout: "header" | "parent" | "child" = "parent";
    @Input() noActiveTab = false;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onCloseTab = new EventEmitter();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onMouseEnterTab = new EventEmitter();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onMouseLeaveTab = new EventEmitter();
    overriddenTabs: ITab[];
    @Input() parameterName: string;
    @Input() routePushHistory = true;
    @Input() size = 4;
    @Input() tabClass: string;
    @Input() updateRouteOnLoad = false;
    @Input() useRoute = true;
    @Input() wrap = false;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly destroy$ = new Subject<void>();
    private readonly router = inject(Router);

    @HostBinding("class.child") get hasChildLayout() {
        return this.layout === "child";
    }

    @HostBinding("class.header") get hasHeaderLayout() {
        return this.layout === "header";
    }

    @HostBinding("class.parent") get hasParentLayout() {
        return this.layout === "parent";
    }

    @Input()
    get selectedTab() {
        return this.selectedTab;
    }

    set selectedTab(value) {
        this.selectTab(value, false);
    }

    @Input()
    get selectedTabId() {
        return this.activeTab?.tabId;
    }

    set selectedTabId(value) {
        this.selectTabById(value);
    }

    @Input()
    get tabs() {
        return this.overriddenTabs ? this.overriddenTabs : this.contentTabs?.toArray();
    }

    set tabs(value) {
        this.overriddenTabs = value;
    }

    close(tab: ITab) {
        if (tab.ignoreIconClick === true) {
            this.selectTab(tab, false);
        } else {
            this.onCloseTab.emit(tab);
        }
    }

    init() {
        this.initialized = true;

        if (this.autoSelectNewTab) {
            const previousTabs = { value: this.tabs };

            this.contentTabs.changes.pipe(
                takeUntil(this.destroy$)
            ).subscribe(newTabList => {
                const newTabArray = newTabList.toArray();
                const diff = difference(newTabArray, previousTabs.value);
                const currentTabFromParameters = this.getActiveTab(newTabArray, this.parameterName, this.isQueryParam);
                //Kontrollera om diffen är en för att se om en ny tabb lagts till och den skall då väljas.
                //Annars om diffen är nått annat så behåller vi vald tabb från getActiveTab.
                if (diff.length === 1) {
                    this.selectTab(diff[0], this.useRoute);
                } else {
                    this.selectTab(currentTabFromParameters, this.useRoute);
                }
                previousTabs.value = newTabArray;
            });
        }
        this.setSelectedTab(this.updateRouteOnLoad);
    }

    mouseEnterTab(tab: ITab) {
        // eslint-disable-next-line sonarjs/deprecation
        if (this.onMouseEnterTab.observers.length > 0) {
            this.onMouseEnterTab.emit(tab);
        }
    }

    mouseLeaveTab(tab: ITab) {
        // eslint-disable-next-line sonarjs/deprecation
        if (this.onMouseLeaveTab.observers.length > 0) {
            this.onMouseLeaveTab.emit(tab);
        }
    }

    ngAfterViewInit(): void {
        // skjuter upp init en tick, för att vi inte får ändra state i den här fasen av cykeln
        // (https://angular.io/guide/lifecycle-hooks#responding-to-view-changes)
        setTimeout(() => {
            this.init();
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.initialized) {
            this.setSelectedTab(false);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectTab(tab: ITab, transition: boolean) {
        if (tab.disabled) {
            return;
        }
        if (this.activeTab !== tab && this.activeTab && this.activeTab.deactivate) {
            this.activeTab.deactivate().then((resp) => {
                if (resp) {
                    this.activateTab(tab, transition);
                }
            });
        } else {
            this.activateTab(tab, transition);
        }
    }

    selectTabById(id: any) {
        if (!id) {
            return;
        }

        const foundTab = this.tabs?.find(tab => tab.tabId === id);

        if (this.activeTab === foundTab) {
            return;
        }
        if (foundTab) {
            this.selectTab(foundTab, false);
        }
    }

    setSelectedTab(transition: boolean) {
        if (this.tabs.length === 0) {
            return;
        }

        const currentTab = this.getActiveTab(this.tabs, this.parameterName, this.isQueryParam);
        if (this.noActiveTab) {
            return;
        }
        this.selectTab(currentTab, transition);
    }

    tabsWithBody = () => this.tabs.filter(t => t.gotBody);

    private activateTab(tab: ITab, transition: boolean) {
        if (this.activeTab?.state$) {
            this.activeTab.state$.next("visited");
        }

        this.activeTab = tab;

        if (this.activeTab.state$) {
            this.activeTab.state$.next("active");
        }
        if (this.activeTab.onTabSelected) {
            this.activeTab.onTabSelected.emit();
        }
        if (this.useRoute && transition) {
            this.setRouteParam(tab.routeId, this.parameterName, this.routePushHistory, this.isQueryParam);
        }
        if (tab.dispatchEvent) {
            tab.dispatchEvent();
        }
    }

    private getActiveTab(tabList: ITab[], parameterKey: string, isQueryParam: boolean): ITab {
        if (this.initialTabId) {
            return tabList.find(x => x.tabId === this.initialTabId);
        }

        let parameter = "";
        if (isQueryParam) {
            parameter = this.activatedRoute.snapshot.queryParamMap.get(parameterKey);
        } else {
            parameter = this.activatedRoute.snapshot.paramMap.get(parameterKey);
        }
        const current = tabList.filter(t => t.routeId !== undefined && t.routeId === parameter);
        return current.length > 0 ? current[0] : tabList[0];
    }

    private setRouteParam(value: any, parameterKey: string, pushHistory: boolean, isQueryParam: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (isQueryParam) {
            const params = {};
            params[parameterKey] = value;

            this.router.navigate([], {
                replaceUrl: !pushHistory,
                queryParams: params,
                skipLocationChange: false,
                queryParamsHandling: "merge"
            });
        } else {
            this.router.navigate([
                "../",
                value ?? ""
            ], {
                replaceUrl: !pushHistory,
                skipLocationChange: false,
                relativeTo: this.activatedRoute
            });
        }
    }
}
